import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { 
  IconClipboardList, IconTruckDelivery, IconRefresh, 
  IconUsers, IconPlus, IconShirt, IconDroplet, IconDeviceDesktop, 
  IconHanger, IconCheck, IconAlertTriangle, IconSearch, 
  IconFileText, IconPrinter, IconDownload, IconFileInvoice, 
  IconArrowsExchange, IconX, IconEdit, IconBuildingWarehouse, 
  IconEye, IconCalendar, IconBuildingStore, IconAlertCircle
} from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";
import * as laundryApi from "../controllers/laundry.controller";
import { useStoreSettings } from "../controllers/settings.controller";

export default function LaundryPage() {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs";

  const [tab, setTab] = useState("dashboard"); 
  // dashboard, inventory, dispatches, usage, vendors, reconciliations, reports, guest_orders, batches, machines, uniforms
  const [loading, setLoading] = useState(false);

  // Core Data
  const [items, setItems] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [reportsData, setReportsData] = useState(null);
  const [guestOrders, setGuestOrders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [machines, setMachines] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [uniforms, setUniforms] = useState([]);

  // Modals & Active Selections
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [itemMovements, setItemMovements] = useState([]);
  const [dispatchReceipts, setDispatchReceipts] = useState([]);
  const [vendorRatesList, setVendorRatesList] = useState([]);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [reportDateRange, setReportDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10)
  });

  // Forms
  const [itemForm, setItemForm] = useState({
    name: "", category: "Bed Linen", unit: "Pcs", stock: 0, threshold: 5, par_level: 10, unit_cost: 0
  });

  const [conditionTransferForm, setConditionTransferForm] = useState({
    itemId: "", fromCondition: "dirty", toCondition: "clean", qty: 1, reason: "Internal Laundry Wash"
  });

  const [usageForm, setUsageForm] = useState({
    room_id: "", room_name: "", department: "Housekeeping", staff_name: "", type: "Issue", notes: "",
    items: [{ item_id: "", name: "", quantity: 1 }]
  });

  const [vendorForm, setVendorForm] = useState({
    name: "", phone: "", address: "", supplier_id: "", contact_person: "", email: "", pan_no: "",
    billing_basis: "per_piece", default_rate: 0, rate_contract_ref: "", payment_terms: "Net 30", notes: ""
  });

  const [dispatchForm, setDispatchForm] = useState({
    vendor_id: "", dispatch_date: new Date().toISOString().slice(0, 16),
    expected_return_date: new Date(Date.now() + 48 * 3600 * 1000).toISOString().slice(0, 16),
    dispatch_person: "", receiving_person: "", challan_no: "", billing_basis: "per_piece",
    total_weight_kg: 0, notes: "", pre_damage_notes: "",
    items: [{ item_id: "", quantity: 1, unit: "Pcs", unit_rate: 0 }]
  });

  const [deliveryReceiptForm, setDeliveryReceiptForm] = useState({
    receipt_no: "", delivery_date: new Date().toISOString().slice(0, 16),
    vendor_delivery_person: "", received_by: "", notes: "",
    items: []
  });

  const [vendorBillForm, setVendorBillForm] = useState({
    invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10),
    amount: 0, notes: ""
  });

  const [reconciliationForm, setReconciliationForm] = useState({
    dispatch_id: "", item_id: "", discrepancy_type: "shortage",
    discrepancy_qty: 1, estimated_loss: 0, notes: ""
  });

  const [resolveReconciliationForm, setResolveReconciliationForm] = useState({
    resolution_action: "approved_writeoff", supervisor_notes: ""
  });

  // Guest Order / Batches / Machine / Chemical / Uniform Forms
  const [newGuestOrderForm, setNewGuestOrderForm] = useState({ room_id: "", guest_name: "", items: "", total_price: 0 });
  const [newBatchForm, setNewBatchForm] = useState({ batch_type: "Washing", weight_kg: 0, machine_id: "" });
  const [newMachineForm, setNewMachineForm] = useState({ name: "", type: "Washer" });
  const [newChemicalForm, setNewChemicalForm] = useState({ name: "", stock: 0, unit: "Liters" });
  const [newUniformForm, setNewUniformForm] = useState({ staff_name: "", uniform_type: "" });

  useEffect(() => {
    fetchTabData();
  }, [tab]);

  // Load auxiliary lists on mount
  useEffect(() => {
    fetchSuppliersAndRooms();
  }, []);

  const fetchSuppliersAndRooms = async () => {
    try {
      const [supRes, roomRes] = await Promise.allSettled([
        apiClient.get("/suppliers"),
        apiClient.get("/rooms")
      ]);
      if (supRes.status === "fulfilled" && supRes.value.data) {
        setSuppliers(Array.isArray(supRes.value.data) ? supRes.value.data : supRes.value.data.suppliers || []);
      }
      if (roomRes.status === "fulfilled" && roomRes.value.data) {
        setRooms(Array.isArray(roomRes.value.data) ? roomRes.value.data : roomRes.value.data.rooms || []);
      }
    } catch (e) {
      console.warn("Auxiliary load error", e);
    }
  };

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (tab === "dashboard") {
        const stats = await laundryApi.getLaundryDashboardStats();
        setDashboardStats(stats && typeof stats === 'object' && !Array.isArray(stats) ? stats : null);
      } else if (tab === "inventory") {
        const res = await laundryApi.getLaundryItems();
        setItems(Array.isArray(res) ? res : []);
      } else if (tab === "dispatches") {
        const [dispRes, vendRes, itemsRes] = await Promise.all([
          laundryApi.getLaundryDispatches(),
          laundryApi.getLaundryVendors(),
          laundryApi.getLaundryItems()
        ]);
        setDispatches(Array.isArray(dispRes) ? dispRes : []);
        setVendors(Array.isArray(vendRes) ? vendRes : []);
        setItems(Array.isArray(itemsRes) ? itemsRes : []);
      } else if (tab === "usage") {
        const [usageRes, itemsRes] = await Promise.all([
          laundryApi.getLaundryRoomUsage(),
          laundryApi.getLaundryItems()
        ]);
        setUsageLogs(Array.isArray(usageRes) ? usageRes : []);
        setItems(Array.isArray(itemsRes) ? itemsRes : []);
      } else if (tab === "vendors") {
        const res = await laundryApi.getLaundryVendors();
        setVendors(Array.isArray(res) ? res : []);
      } else if (tab === "reconciliations") {
        const [recRes, itemsRes, dispRes] = await Promise.all([
          laundryApi.getReconciliations(),
          laundryApi.getLaundryItems(),
          laundryApi.getLaundryDispatches()
        ]);
        setReconciliations(Array.isArray(recRes) ? recRes : []);
        setItems(Array.isArray(itemsRes) ? itemsRes : []);
        setDispatches(Array.isArray(dispRes) ? dispRes : []);
      } else if (tab === "reports") {
        const rep = await laundryApi.getLaundryReports({
          from_date: reportDateRange.from,
          to_date: reportDateRange.to
        });
        setReportsData(rep && typeof rep === 'object' ? rep : null);
      } else if (tab === "guest_orders") {
        const res = await laundryApi.getGuestOrders();
        setGuestOrders(Array.isArray(res) ? res : []);
      } else if (tab === "batches") {
        const [batchRes, machRes] = await Promise.all([
          laundryApi.getBatches(),
          laundryApi.getMachines()
        ]);
        setBatches(Array.isArray(batchRes) ? batchRes : []);
        setMachines(Array.isArray(machRes) ? machRes : []);
      } else if (tab === "machines") {
        const [machRes, chemRes] = await Promise.all([
          laundryApi.getMachines(),
          laundryApi.getChemicals()
        ]);
        setMachines(Array.isArray(machRes) ? machRes : []);
        setChemicals(Array.isArray(chemRes) ? chemRes : []);
      } else if (tab === "uniforms") {
        const res = await laundryApi.getUniforms();
        setUniforms(Array.isArray(res) ? res : []);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load laundry data");
    } finally {
      setLoading(false);
    }
  };

  // Helper status color
  const getStatusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (["clean", "ready", "returned", "delivered", "available", "active", "approved_writeoff", "paid"].includes(s)) {
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800";
    }
    if (["dirty", "requested", "washing", "pending", "issued", "dispatched", "pending_review", "partially returned"].includes(s)) {
      return "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200 dark:border-amber-800";
    }
    if (["drying", "ironing", "collected", "in_transit", "billed_to_vendor"].includes(s)) {
      return "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200 dark:border-blue-800";
    }
    if (["damaged", "lost", "out of order", "overdue", "reconciliation required", "rejected"].includes(s)) {
      return "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200 dark:border-rose-800";
    }
    return "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-gray-300 border border-gray-200 dark:border-zinc-700";
  };

  // Condition transfer submission
  const handleConditionTransfer = async (e) => {
    e.preventDefault();
    try {
      await laundryApi.adjustLinenCondition({
        item_id: conditionTransferForm.itemId,
        from_condition: conditionTransferForm.fromCondition,
        to_condition: conditionTransferForm.toCondition,
        qty: Number(conditionTransferForm.qty),
        notes: conditionTransferForm.reason
      });
      toast.success("Stock transferred between conditions successfully");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Condition transfer failed");
    }
  };

  // New linen item
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await laundryApi.updateLaundryItem(selectedItem.id, itemForm);
        toast.success("Linen item updated");
      } else {
        await laundryApi.createLaundryItem(itemForm);
        toast.success("Linen item created");
      }
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save linen item");
    }
  };

  // New room usage log
  const handleUsageSubmit = async (e) => {
    e.preventDefault();
    try {
      const validItems = usageForm.items.filter(i => i.item_id && Number(i.quantity) > 0);
      if (validItems.length === 0) {
        toast.error("Please add at least one linen item with valid quantity");
        return;
      }
      await laundryApi.logLaundryRoomUsage({
        room_id: usageForm.room_id || null,
        room_name: usageForm.room_name || usageForm.department,
        staff_name: usageForm.staff_name,
        type: usageForm.type,
        notes: usageForm.notes,
        items: validItems
      });
      toast.success("Room usage recorded successfully");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record usage");
    }
  };

  // Vendor save
  const handleVendorSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedVendor) {
        await laundryApi.updateLaundryVendor(selectedVendor.id, vendorForm);
        toast.success("Vendor updated successfully");
      } else {
        await laundryApi.createLaundryVendor(vendorForm);
        toast.success("Vendor created successfully");
      }
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save vendor");
    }
  };

  // Dispatch create
  const handleDispatchSubmit = async (e) => {
    e.preventDefault();
    try {
      const validItems = dispatchForm.items.filter(i => i.item_id && Number(i.quantity) > 0);
      if (validItems.length === 0) {
        toast.error("Please add at least one line item with quantity");
        return;
      }
      await laundryApi.createLaundryDispatch({
        vendor_id: dispatchForm.vendor_id,
        dispatch_date: dispatchForm.dispatch_date,
        expected_return_date: dispatchForm.expected_return_date,
        dispatch_person: dispatchForm.dispatch_person,
        receiving_person: dispatchForm.receiving_person,
        challan_no: dispatchForm.challan_no,
        billing_basis: dispatchForm.billing_basis,
        total_weight_kg: Number(dispatchForm.total_weight_kg || 0),
        notes: dispatchForm.notes,
        pre_damage_notes: dispatchForm.pre_damage_notes,
        items: validItems
      });
      toast.success("Laundry dispatched to vendor successfully");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to dispatch laundry");
    }
  };

  // Open Receive Delivery modal
  const handleOpenReceiveModal = async (disp) => {
    try {
      const details = await laundryApi.getLaundryDispatchDetails(disp.id);
      setSelectedDispatch(details);
      setDeliveryReceiptForm({
        receipt_no: `REC-${Date.now().toString().slice(-6)}`,
        delivery_date: new Date().toISOString().slice(0, 16),
        vendor_delivery_person: "",
        received_by: "",
        notes: "",
        items: (details.items || []).map(it => ({
          item_id: it.item_id,
          name: it.item_name,
          unit: it.unit,
          dispatched_qty: it.dispatched_qty,
          outstanding_qty: it.outstanding_qty,
          accepted_clean_qty: it.outstanding_qty > 0 ? it.outstanding_qty : 0,
          rewash_qty: 0,
          damaged_qty: 0,
          missing_qty: 0
        }))
      });
      setModalType("receive_delivery");
      setIsModalOpen(true);
    } catch (err) {
      toast.error("Failed to load dispatch details");
    }
  };

  // Submit delivery receipt
  const handleDeliveryReceiptSubmit = async (e) => {
    e.preventDefault();
    try {
      await laundryApi.receiveLaundryDispatch(selectedDispatch.id, {
        receipt_no: deliveryReceiptForm.receipt_no,
        delivery_date: deliveryReceiptForm.delivery_date,
        vendor_delivery_person: deliveryReceiptForm.vendor_delivery_person,
        received_by: deliveryReceiptForm.received_by,
        notes: deliveryReceiptForm.notes,
        items: deliveryReceiptForm.items
      });
      toast.success("Delivery received and stock updated!");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to receive delivery");
    }
  };

  // Open delivery history
  const handleOpenDeliveryHistory = async (disp) => {
    try {
      setSelectedDispatch(disp);
      const receipts = await laundryApi.getDispatchReceipts(disp.id);
      setDispatchReceipts(receipts || []);
      setModalType("delivery_history");
      setIsModalOpen(true);
    } catch (err) {
      toast.error("Failed to fetch delivery receipts");
    }
  };

  // Open Generate Bill modal
  const handleOpenGenerateBill = (disp) => {
    setSelectedDispatch(disp);
    const est = Number(disp.actual_cost || disp.estimated_cost || 0);
    setVendorBillForm({
      invoice_no: `INV-${disp.dispatch_no || disp.id}`,
      invoice_date: new Date().toISOString().slice(0, 10),
      amount: est > 0 ? est : 100,
      notes: `Laundry washing charges for dispatch ${disp.dispatch_no || disp.id}`
    });
    setModalType("generate_bill");
    setIsModalOpen(true);
  };

  // Submit Vendor Bill to Accounts Payable
  const handleVendorBillSubmit = async (e) => {
    e.preventDefault();
    try {
      await laundryApi.generateVendorBill(selectedDispatch.id, vendorBillForm);
      toast.success("Vendor invoice added to Accounts Payable (Purchases) successfully!");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate vendor bill");
    }
  };

  // Open Vendor Rate Cards
  const handleOpenVendorRates = async (vend) => {
    try {
      setSelectedVendor(vend);
      const rates = await laundryApi.getVendorRates(vend.id);
      // Map all items with their rate or 0
      const currentItems = Array.isArray(items) && items.length > 0 ? items : await laundryApi.getLaundryItems();
      const safeItems = Array.isArray(currentItems) ? currentItems : [];
      const safeRates = Array.isArray(rates) ? rates : [];
      const mapped = safeItems.map(it => {
        const found = safeRates.find(r => r.item_id === it.id);
        return {
          item_id: it.id,
          name: it.name,
          category: it.category,
          unit: it.unit,
          rate: found ? found.rate : (vend.default_rate || 0),
          billing_basis: found ? found.billing_basis : (vend.billing_basis || "per_piece")
        };
      });
      setVendorRatesList(mapped);
      setModalType("vendor_rates");
      setIsModalOpen(true);
    } catch (err) {
      toast.error("Failed to load vendor rates");
    }
  };

  // Save Vendor Rates
  const handleSaveVendorRates = async (e) => {
    e.preventDefault();
    try {
      await laundryApi.saveVendorRates(selectedVendor.id, vendorRatesList);
      toast.success("Rate card saved successfully");
      setIsModalOpen(false);
    } catch (err) {
      toast.error("Failed to save rate card");
    }
  };

  // Open item movements history
  const handleOpenItemHistory = async (it) => {
    try {
      setSelectedItem(it);
      const rows = await laundryApi.getLinenMovements(it.id);
      setItemMovements(rows || []);
      setModalType("item_history");
      setIsModalOpen(true);
    } catch (err) {
      toast.error("Failed to load item movement history");
    }
  };

  // Resolve reconciliation
  const handleResolveReconciliation = async (e) => {
    e.preventDefault();
    try {
      await laundryApi.approveReconciliation(selectedReconciliation.id, resolveReconciliationForm);
      toast.success("Discrepancy resolved successfully");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resolve discrepancy");
    }
  };

  // Create manual reconciliation
  const handleCreateReconciliation = async (e) => {
    e.preventDefault();
    try {
      await laundryApi.createReconciliation({
        dispatch_id: reconciliationForm.dispatch_id || null,
        item_id: reconciliationForm.item_id,
        discrepancy_type: reconciliationForm.discrepancy_type,
        discrepancy_qty: Number(reconciliationForm.discrepancy_qty),
        estimated_loss: Number(reconciliationForm.estimated_loss || 0),
        notes: reconciliationForm.notes
      });
      toast.success("Discrepancy record submitted");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit discrepancy");
    }
  };

  // Handle generic forms for other tabs
  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === "guest_order") {
        await laundryApi.createGuestOrder(newGuestOrderForm);
        toast.success("Guest order created");
      } else if (modalType === "batch") {
        await laundryApi.createBatch(newBatchForm);
        toast.success("Batch logged");
      } else if (modalType === "machine") {
        await laundryApi.createMachine(newMachineForm);
        toast.success("Machine added");
      } else if (modalType === "chemical") {
        await laundryApi.createChemical(newChemicalForm);
        toast.success("Chemical added");
      } else if (modalType === "uniform") {
        await laundryApi.createUniform(newUniformForm);
        toast.success("Uniform issued");
      }
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save record");
    }
  };

  // Status update handler for guest orders, batches, uniforms
  const updateStatus = async (type, id, status) => {
    try {
      if (type === "guest_order") await laundryApi.updateGuestOrderStatus(id, status);
      else if (type === "batch") await laundryApi.updateBatchStatus(id, status);
      else if (type === "uniform") await laundryApi.updateUniformStatus(id, status);
      toast.success("Status updated");
      fetchTabData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  // Filtered Items
  const filteredItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items.filter(it => {
      const matchesSearch = (it.name || "").toLowerCase().includes(searchQuery.toLowerCase()) || 
                            (it.category || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === "all" || it.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchQuery, categoryFilter]);

  // Export report to CSV
  const handleExportCSV = () => {
    if (!reportsData) return;
    let csv = "Item Name,Category,Unit,Clean Stock,Issued,Dirty,In Transit,Rewash,Damaged,Missing,Total Stock\n";
    (reportsData.condition_breakdown || []).forEach(r => {
      csv += `"${r.name}","${r.category || 'General'}","${r.unit || 'Pcs'}",${r.clean_stock || 0},${r.issued_stock || 0},${r.dirty_stock || 0},${r.in_transit_vendor_stock || 0},${r.rewash_stock || 0},${r.damaged_stock || 0},${r.missing_stock || 0},${r.total_stock || 0}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `laundry_linen_report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="p-4 md:p-6 min-h-screen bg-restro-green-light dark:bg-restro-gray text-restro-text dark:text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Laundry & Linen Management
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-restro-green/10 text-restro-green dark:bg-restro-green/20">
              eHospitality
            </span>
          </div>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
            Complete inventory condition tracking, room exchanges, vendor contracts & multi-delivery partial receipts.
          </p>
        </div>

        {/* Global Action Buttons */}
        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={() => {
              if (tab === "inventory") {
                setSelectedItem(null);
                setItemForm({ name: "", category: "Bed Linen", unit: "Pcs", stock: 0, threshold: 5, par_level: 10, unit_cost: 0 });
                setModalType("add_item");
              } else if (tab === "dispatches") {
                setDispatchForm({
                  vendor_id: vendors[0]?.id || "",
                  dispatch_date: new Date().toISOString().slice(0, 16),
                  expected_return_date: new Date(Date.now() + 48 * 3600 * 1000).toISOString().slice(0, 16),
                  dispatch_person: "", receiving_person: "", challan_no: "", billing_basis: "per_piece",
                  total_weight_kg: 0, notes: "", pre_damage_notes: "",
                  items: [{ item_id: items[0]?.id || "", quantity: 1, unit: "Pcs", unit_rate: 0 }]
                });
                setModalType("new_dispatch");
              } else if (tab === "usage") {
                setUsageForm({
                  room_id: "", room_name: "", department: "Housekeeping", staff_name: "", type: "Issue", notes: "",
                  items: [{ item_id: items[0]?.id || "", name: items[0]?.name || "", quantity: 1 }]
                });
                setModalType("add_usage");
              } else if (tab === "vendors") {
                setSelectedVendor(null);
                setVendorForm({
                  name: "", phone: "", address: "", supplier_id: "", contact_person: "", email: "", pan_no: "",
                  billing_basis: "per_piece", default_rate: 0, rate_contract_ref: "", payment_terms: "Net 30", notes: ""
                });
                setModalType("add_vendor");
              } else if (tab === "reconciliations") {
                setReconciliationForm({
                  dispatch_id: "", item_id: items[0]?.id || "", discrepancy_type: "shortage",
                  discrepancy_qty: 1, estimated_loss: 0, notes: ""
                });
                setModalType("report_reconciliation");
              } else if (tab === "machines") {
                setModalType("machine");
              } else if (tab === "guest_orders") {
                setModalType("guest_order");
              } else if (tab === "batches") {
                setModalType("batch");
              } else if (tab === "uniforms") {
                setModalType("uniform");
              } else {
                setConditionTransferForm({
                  itemId: items[0]?.id || "",
                  fromCondition: "dirty",
                  toCondition: "clean",
                  qty: 1,
                  reason: "Condition Adjustment"
                });
                setModalType("condition_transfer");
              }
              setIsModalOpen(true);
            }}
            className="px-4 py-2.5 rounded-2xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold flex items-center gap-2 shadow-sm transition text-sm"
          >
            <IconPlus size={18} />
            {tab === "inventory" ? "Add Linen Item" :
             tab === "dispatches" ? "New Vendor Dispatch" :
             tab === "usage" ? "Log Room Issue/Collect" :
             tab === "vendors" ? "Register Vendor" :
             tab === "reconciliations" ? "Report Discrepancy" :
             tab === "guest_orders" ? "New Guest Order" :
             tab === "batches" ? "New Batch" :
             tab === "machines" ? "Add Machine" :
             tab === "uniforms" ? "Issue Uniform" : "Transfer Stock"}
          </button>

          <button
            onClick={() => {
              setConditionTransferForm({
                itemId: items[0]?.id || "",
                fromCondition: "dirty",
                toCondition: "clean",
                qty: 1,
                reason: "Laundry Wash Return"
              });
              setModalType("condition_transfer");
              setIsModalOpen(true);
            }}
            className="px-3.5 py-2.5 rounded-2xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-200 font-bold flex items-center gap-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-zinc-700 transition text-sm"
          >
            <IconArrowsExchange size={18} className="text-restro-green" />
            Transfer Condition
          </button>

          <button
            onClick={fetchTabData}
            className="p-2.5 rounded-2xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-gray-300 hover:text-restro-green transition"
            title="Refresh"
          >
            <IconRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 dark:border-zinc-800 gap-1 md:gap-3 mb-6 overflow-x-auto no-scrollbar">
        {[
          { id: "dashboard", label: "Dashboard", icon: <IconBuildingWarehouse size={17} /> },
          { id: "inventory", label: "Linen Inventory", icon: <IconClipboardList size={17} /> },
          { id: "dispatches", label: "Vendor Dispatches", icon: <IconTruckDelivery size={17} /> },
          { id: "usage", label: "Room Usage", icon: <IconRefresh size={17} /> },
          { id: "vendors", label: "Vendors & Rates", icon: <IconUsers size={17} /> },
          { id: "reconciliations", label: "Audits & Discrepancies", icon: <IconAlertTriangle size={17} /> },
          { id: "reports", label: "Reports", icon: <IconFileText size={17} /> },
          { id: "guest_orders", label: "Guest Orders", icon: <IconShirt size={17} /> },
          { id: "batches", label: "In-house Batches", icon: <IconDroplet size={17} /> },
          { id: "machines", label: "Machines & Chemicals", icon: <IconDeviceDesktop size={17} /> },
          { id: "uniforms", label: "Staff Uniforms", icon: <IconHanger size={17} /> }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-3 px-3 text-xs md:text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition rounded-t-lg ${
              tab === t.id
                ? "border-restro-green text-restro-green bg-restro-green/5 dark:bg-restro-green/10"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center py-28">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-restro-green"></div>
          <p className="text-xs text-gray-400 mt-3 font-medium">Syncing laundry & linen status...</p>
        </div>
      ) : (
        <div>
          {/* TAB 1: OPERATIONS DASHBOARD */}
          {tab === "dashboard" && dashboardStats && (
            <div className="space-y-6">
              {/* Overdue alert banner if any */}
              {Number(dashboardStats.dispatches?.overdue_count || 0) > 0 && (
                <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-300 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IconAlertCircle className="text-rose-600" size={24} />
                    <div>
                      <p className="font-bold text-sm">
                        {dashboardStats.dispatches.overdue_count} Vendor Dispatch(es) Overdue for Return!
                      </p>
                      <p className="text-xs text-rose-600 dark:text-rose-400">
                        External laundry has exceeded agreed turnaround time. Please inspect vendor delivery or contact supplier.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setTab("dispatches")}
                    className="px-3.5 py-1.5 rounded-xl bg-rose-600 text-white text-xs font-bold hover:bg-rose-700 transition"
                  >
                    View Dispatches
                  </button>
                </div>
              )}

              {/* KPI Cards Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Total Pieces</span>
                  <div className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                    {dashboardStats.linen_stock?.total_stock || 0}
                  </div>
                  <span className="text-[10px] text-gray-400">All condition stages</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Clean & Ready</span>
                  <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                    {dashboardStats.linen_stock?.clean_stock || 0}
                  </div>
                  <span className="text-[10px] text-emerald-500">Available for rooms</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-blue-100 dark:border-blue-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Issued to Rooms</span>
                  <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                    {dashboardStats.linen_stock?.issued_stock || 0}
                  </div>
                  <span className="text-[10px] text-blue-500">Currently in guest rooms</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-amber-100 dark:border-amber-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">Dirty Awaiting</span>
                  <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                    {dashboardStats.linen_stock?.dirty_stock || 0}
                  </div>
                  <span className="text-[10px] text-amber-500">Ready to dispatch</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-purple-100 dark:border-purple-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">With Vendors</span>
                  <div className="text-2xl font-black text-purple-600 dark:text-purple-400 mt-1">
                    {dashboardStats.linen_stock?.in_transit_vendor_stock || 0}
                  </div>
                  <span className="text-[10px] text-purple-500">Out for laundering</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-rose-100 dark:border-rose-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">Damaged / Rewash</span>
                  <div className="text-2xl font-black text-rose-600 dark:text-rose-400 mt-1">
                    {(Number(dashboardStats.linen_stock?.damaged_stock || 0) + Number(dashboardStats.linen_stock?.rewash_stock || 0))}
                  </div>
                  <span className="text-[10px] text-rose-500">Under repair or rejection</span>
                </div>
              </div>

              {/* Vendor & Operations Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Vendor Dispatches Summary</h3>
                    <span className="p-2 rounded-xl bg-purple-50 text-purple-600 dark:bg-purple-950/50"><IconTruckDelivery size={18} /></span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-gray-50 dark:border-zinc-800/50">
                      <span className="text-gray-500">Active Dispatched Batches:</span>
                      <span className="font-bold">{dashboardStats.dispatches?.active_count || 0}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-50 dark:border-zinc-800/50">
                      <span className="text-gray-500">Outstanding Pieces with Vendors:</span>
                      <span className="font-bold text-purple-600">{dashboardStats.dispatches?.total_outstanding || 0} pcs</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Overdue Batches:</span>
                      <span className="font-bold text-rose-600">{dashboardStats.dispatches?.overdue_count || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Accounts Payable & Costs</h3>
                    <span className="p-2 rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50"><IconFileInvoice size={18} /></span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-gray-50 dark:border-zinc-800/50">
                      <span className="text-gray-500">Total Unpaid AP Bills:</span>
                      <span className="font-bold text-emerald-600">{currency} {Number(dashboardStats.financials?.total_due || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-50 dark:border-zinc-800/50">
                      <span className="text-gray-500">Total Billed Laundry (30d):</span>
                      <span className="font-bold">{currency} {Number(dashboardStats.financials?.total_billed || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Pending Invoice Approvals:</span>
                      <span className="font-bold">{dashboardStats.financials?.unpaid_bills_count || 0} invoices</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Quality & Reconciliations</h3>
                    <span className="p-2 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-950/50"><IconAlertTriangle size={18} /></span>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between py-1 border-b border-gray-50 dark:border-zinc-800/50">
                      <span className="text-gray-500">Pending Discrepancy Audits:</span>
                      <span className="font-bold text-amber-600">{dashboardStats.reconciliations?.pending_count || 0}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-50 dark:border-zinc-800/50">
                      <span className="text-gray-500">Unresolved Shortage/Damage Loss:</span>
                      <span className="font-bold text-rose-600">{currency} {Number(dashboardStats.reconciliations?.pending_loss || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Missing Linen Items:</span>
                      <span className="font-bold">{dashboardStats.linen_stock?.missing_stock || 0} pcs</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions Bar */}
              <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3">Frequently Used Workflows</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <button
                    onClick={() => { setTab("dispatches"); }}
                    className="p-3.5 rounded-2xl bg-gray-50 dark:bg-zinc-800/80 hover:bg-gray-100 dark:hover:bg-zinc-800 text-left transition flex items-center gap-3 border border-gray-200/50 dark:border-zinc-700/50"
                  >
                    <span className="p-2 rounded-xl bg-purple-500/10 text-purple-600"><IconTruckDelivery size={20} /></span>
                    <div>
                      <p className="font-bold text-xs">Vendor Dispatches</p>
                      <p className="text-[10px] text-gray-400">Receive & Challan</p>
                    </div>
                  </button>

                  <button
                    onClick={() => { setTab("usage"); }}
                    className="p-3.5 rounded-2xl bg-gray-50 dark:bg-zinc-800/80 hover:bg-gray-100 dark:hover:bg-zinc-800 text-left transition flex items-center gap-3 border border-gray-200/50 dark:border-zinc-700/50"
                  >
                    <span className="p-2 rounded-xl bg-blue-500/10 text-blue-600"><IconRefresh size={20} /></span>
                    <div>
                      <p className="font-bold text-xs">Room Exchanges</p>
                      <p className="text-[10px] text-gray-400">Issue / Collect</p>
                    </div>
                  </button>

                  <button
                    onClick={() => { setTab("reconciliations"); }}
                    className="p-3.5 rounded-2xl bg-gray-50 dark:bg-zinc-800/80 hover:bg-gray-100 dark:hover:bg-zinc-800 text-left transition flex items-center gap-3 border border-gray-200/50 dark:border-zinc-700/50"
                  >
                    <span className="p-2 rounded-xl bg-amber-500/10 text-amber-600"><IconAlertTriangle size={20} /></span>
                    <div>
                      <p className="font-bold text-xs">Discrepancy Audits</p>
                      <p className="text-[10px] text-gray-400">Loss & Write-offs</p>
                    </div>
                  </button>

                  <button
                    onClick={() => { setTab("reports"); }}
                    className="p-3.5 rounded-2xl bg-gray-50 dark:bg-zinc-800/80 hover:bg-gray-100 dark:hover:bg-zinc-800 text-left transition flex items-center gap-3 border border-gray-200/50 dark:border-zinc-700/50"
                  >
                    <span className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600"><IconFileText size={20} /></span>
                    <div>
                      <p className="font-bold text-xs">Linen Reports</p>
                      <p className="text-[10px] text-gray-400">Turnaround & Costs</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: LINEN INVENTORY & CONDITION BREAKDOWN */}
          {tab === "inventory" && (
            <div className="space-y-4">
              {/* Filter and Search Bar */}
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="relative flex-1">
                  <IconSearch size={16} className="absolute left-3.5 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search linen by name or category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs md:text-sm focus:outline-none focus:border-restro-green"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="py-2.5 px-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs md:text-sm focus:outline-none"
                  >
                    <option value="all">All Categories</option>
                    <option value="Bed Linen">Bed Linen</option>
                    <option value="Bath Linen">Bath Linen</option>
                    <option value="Table Linen">Table Linen</option>
                    <option value="Uniforms">Uniforms</option>
                    <option value="General">General Textiles</option>
                  </select>
                </div>
              </div>

              {/* Inventory Table */}
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Item & Category</th>
                        <th className="p-3.5 text-center text-emerald-600 dark:text-emerald-400">Clean</th>
                        <th className="p-3.5 text-center text-blue-600 dark:text-blue-400">In Rooms</th>
                        <th className="p-3.5 text-center text-amber-600 dark:text-amber-400">Dirty</th>
                        <th className="p-3.5 text-center text-purple-600 dark:text-purple-400">In-Transit</th>
                        <th className="p-3.5 text-center text-orange-600 dark:text-orange-400">Rewash</th>
                        <th className="p-3.5 text-center text-rose-600 dark:text-rose-400">Damaged</th>
                        <th className="p-3.5 text-center text-gray-400">Missing</th>
                        <th className="p-3.5 text-center font-black">Total Stock</th>
                        <th className="p-3.5 text-center">PAR Level</th>
                        <th className="p-3.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {filteredItems.map(item => {
                        const isLowPar = (item.clean_stock || 0) < (item.par_level || 0);
                        return (
                          <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                            <td className="p-3.5">
                              <div className="font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                {item.name}
                                {isLowPar && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-300">
                                    LOW PAR
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-gray-400">
                                {item.category || "General"} • {item.unit || "Pcs"} • {currency} {item.unit_cost || 0}
                              </span>
                            </td>
                            <td className="p-3.5 text-center font-bold text-emerald-600 dark:text-emerald-400">
                              {item.clean_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-blue-600 dark:text-blue-400">
                              {item.issued_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-amber-600 dark:text-amber-400">
                              {item.dirty_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-purple-600 dark:text-purple-400">
                              {item.in_transit_vendor_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-orange-600 dark:text-orange-400">
                              {item.rewash_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-rose-600 dark:text-rose-400">
                              {item.damaged_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-gray-400">
                              {item.missing_stock || 0}
                            </td>
                            <td className="p-3.5 text-center font-black text-gray-900 dark:text-white">
                              {item.stock || 0}
                            </td>
                            <td className="p-3.5 text-center text-gray-500 font-medium">
                              {item.par_level || 0}
                            </td>
                            <td className="p-3.5 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  onClick={() => {
                                    setConditionTransferForm({
                                      itemId: item.id,
                                      fromCondition: "dirty",
                                      toCondition: "clean",
                                      qty: 1,
                                      reason: "Condition Adjustment"
                                    });
                                    setModalType("condition_transfer");
                                    setIsModalOpen(true);
                                  }}
                                  className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 transition"
                                  title="Transfer between conditions"
                                >
                                  <IconArrowsExchange size={15} />
                                </button>

                                <button
                                  onClick={() => handleOpenItemHistory(item)}
                                  className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 hover:bg-blue-100 transition"
                                  title="Audit Movements History"
                                >
                                  <IconEye size={15} />
                                </button>

                                <button
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setItemForm({
                                      name: item.name,
                                      category: item.category || "Bed Linen",
                                      unit: item.unit || "Pcs",
                                      stock: item.stock || 0,
                                      threshold: item.threshold || 5,
                                      par_level: item.par_level || 10,
                                      unit_cost: item.unit_cost || 0
                                    });
                                    setModalType("edit_item");
                                    setIsModalOpen(true);
                                  }}
                                  className="p-1.5 rounded-lg bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 transition"
                                  title="Edit Item"
                                >
                                  <IconEdit size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {filteredItems.length === 0 && (
                        <tr>
                          <td colSpan={11} className="p-8 text-center text-gray-400">
                            No linen items found. Click "Add Linen Item" to register textiles.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: VENDOR DISPATCHES */}
          {tab === "dispatches" && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Dispatch Ref & Vendor</th>
                        <th className="p-3.5">Dispatch Date</th>
                        <th className="p-3.5">Expected Return</th>
                        <th className="p-3.5 text-center">Dispatched</th>
                        <th className="p-3.5 text-center text-emerald-600">Accepted</th>
                        <th className="p-3.5 text-center text-orange-600">Rewash</th>
                        <th className="p-3.5 text-center text-rose-600">Damaged</th>
                        <th className="p-3.5 text-center text-purple-600 font-black">Outstanding</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5 text-center">AP Invoice</th>
                        <th className="p-3.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {(Array.isArray(dispatches) ? dispatches : []).map(disp => {
                        const isOverdue = disp.status !== "Returned" && new Date(disp.expected_return_date) < new Date();
                        return (
                          <tr key={disp.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                            <td className="p-3.5">
                              <div className="font-bold text-gray-900 dark:text-white">
                                {disp.dispatch_no || `DISP-${disp.id}`}
                              </div>
                              <span className="text-[11px] text-gray-400 font-medium">
                                {disp.vendor_name || "External Vendor"} {disp.challan_no ? `• Challan: ${disp.challan_no}` : ""}
                              </span>
                            </td>
                            <td className="p-3.5 text-gray-500 text-xs">
                              {new Date(disp.dispatch_date).toLocaleDateString()} {new Date(disp.dispatch_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="p-3.5">
                              <span className={`text-xs ${isOverdue ? "text-rose-600 font-bold" : "text-gray-500"}`}>
                                {disp.expected_return_date ? new Date(disp.expected_return_date).toLocaleDateString() : "N/A"}
                              </span>
                              {isOverdue && (
                                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-black bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400">
                                  OVERDUE
                                </span>
                              )}
                            </td>
                            <td className="p-3.5 text-center font-bold">
                              {disp.total_dispatched_qty || disp.total_qty || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-emerald-600">
                              {disp.total_accepted_qty || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-orange-600">
                              {disp.total_rewash_qty || 0}
                            </td>
                            <td className="p-3.5 text-center font-bold text-rose-600">
                              {disp.total_damaged_qty || 0}
                            </td>
                            <td className="p-3.5 text-center font-black text-purple-600">
                              {disp.total_outstanding_qty !== undefined ? disp.total_outstanding_qty : (disp.total_qty || 0)}
                            </td>
                            <td className="p-3.5 text-center">
                              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(isOverdue && disp.status !== 'Returned' ? 'Overdue' : disp.status)}`}>
                                {isOverdue && disp.status !== 'Returned' ? 'Overdue' : disp.status}
                              </span>
                            </td>
                            <td className="p-3.5 text-center text-xs">
                              {disp.invoice_status === "billed" || disp.purchase_id ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200">
                                  Billed ({disp.invoice_no || `INV-${disp.purchase_id}`})
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleOpenGenerateBill(disp)}
                                  className="px-2 py-1 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 transition"
                                >
                                  + Create Bill
                                </button>
                              )}
                            </td>
                            <td className="p-3.5 text-right whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1.5">
                                {disp.status !== "Returned" && (
                                  <button
                                    onClick={() => handleOpenReceiveModal(disp)}
                                    className="px-2.5 py-1.5 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition flex items-center gap-1 shadow-sm"
                                  >
                                    <IconCheck size={14} /> Receive Delivery
                                  </button>
                                )}

                                <button
                                  onClick={() => handleOpenDeliveryHistory(disp)}
                                  className="p-1.5 rounded-xl bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 transition"
                                  title="Delivery History & Receipts"
                                >
                                  <IconEye size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {dispatches.length === 0 && (
                        <tr>
                          <td colSpan={11} className="p-8 text-center text-gray-400">
                            No external dispatches recorded. Click "New Vendor Dispatch" to send dirty linen for laundering.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: ROOM USAGE LOGS */}
          {tab === "usage" && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Date & Time</th>
                        <th className="p-3.5">Room / Department</th>
                        <th className="p-3.5">Exchange Type</th>
                        <th className="p-3.5">Staff In-Charge</th>
                        <th className="p-3.5">Items & Quantities</th>
                        <th className="p-3.5">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {(Array.isArray(usageLogs) ? usageLogs : []).map(log => (
                        <tr key={log.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                          <td className="p-3.5 text-gray-500 whitespace-nowrap">
                            {new Date(log.created_at).toLocaleDateString()} {new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="p-3.5 font-bold text-gray-900 dark:text-white">
                            {log.room_name || `Room ${log.room_id || "General"}`}
                          </td>
                          <td className="p-3.5">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              log.type === "Issue" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" :
                              log.type === "Collect" ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400" :
                              "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
                            }`}>
                              {log.type || "Exchange"}
                            </span>
                          </td>
                          <td className="p-3.5 text-gray-600 dark:text-gray-300 font-medium">
                            {log.staff_name || "Housekeeping Staff"}
                          </td>
                          <td className="p-3.5 font-medium text-gray-800 dark:text-gray-200">
                            {log.items || "Linen Items"}
                          </td>
                          <td className="p-3.5 text-gray-400 text-xs">
                            {log.notes || "—"}
                          </td>
                        </tr>
                      ))}
                      {usageLogs.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-gray-400">
                            No room linen exchanges recorded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: VENDORS & RATES */}
          {tab === "vendors" && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Vendor Name</th>
                        <th className="p-3.5">Contact Details</th>
                        <th className="p-3.5">Billing Basis</th>
                        <th className="p-3.5">Default Rate</th>
                        <th className="p-3.5">Contract Ref</th>
                        <th className="p-3.5">Linked AP Supplier</th>
                        <th className="p-3.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {(Array.isArray(vendors) ? vendors : []).map(v => (
                        <tr key={v.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                          <td className="p-3.5">
                            <div className="font-bold text-gray-900 dark:text-white">{v.name}</div>
                            <span className="text-[11px] text-gray-400">{v.address || "No address provided"}</span>
                          </td>
                          <td className="p-3.5 text-xs text-gray-600 dark:text-gray-300">
                            <div>{v.contact_person ? `Attn: ${v.contact_person}` : ""}</div>
                            <div>{v.phone || "No phone"}</div>
                            <div className="text-gray-400">{v.email || ""}</div>
                          </td>
                          <td className="p-3.5 font-semibold capitalize text-gray-700 dark:text-gray-300">
                            {(v.billing_basis || "per_piece").replace('_', ' ')}
                          </td>
                          <td className="p-3.5 font-bold text-gray-900 dark:text-white">
                            {currency} {Number(v.default_rate || 0).toFixed(2)}
                          </td>
                          <td className="p-3.5 text-gray-500 text-xs">
                            {v.rate_contract_ref || "Standard Terms"} ({v.payment_terms || "Net 30"})
                          </td>
                          <td className="p-3.5 text-xs">
                            {v.supplier_name ? (
                              <span className="px-2 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200">
                                {v.supplier_name}
                              </span>
                            ) : (
                              <span className="text-gray-400">Not linked to AP</span>
                            )}
                          </td>
                          <td className="p-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleOpenVendorRates(v)}
                                className="px-2.5 py-1.5 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 hover:bg-purple-100 transition text-xs font-bold"
                              >
                                Rate Cards
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedVendor(v);
                                  setVendorForm({
                                    name: v.name,
                                    phone: v.phone || "",
                                    address: v.address || "",
                                    supplier_id: v.supplier_id || "",
                                    contact_person: v.contact_person || "",
                                    email: v.email || "",
                                    pan_no: v.pan_no || "",
                                    billing_basis: v.billing_basis || "per_piece",
                                    default_rate: v.default_rate || 0,
                                    rate_contract_ref: v.rate_contract_ref || "",
                                    payment_terms: v.payment_terms || "Net 30",
                                    notes: v.notes || ""
                                  });
                                  setModalType("edit_vendor");
                                  setIsModalOpen(true);
                                }}
                                className="p-1.5 rounded-xl bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 transition"
                                title="Edit Vendor"
                              >
                                <IconEdit size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {vendors.length === 0 && (
                        <tr>
                          <td colSpan={7} className="p-8 text-center text-gray-400">
                            No external laundry vendors registered yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: AUDITS & RECONCILIATIONS */}
          {tab === "reconciliations" && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Date</th>
                        <th className="p-3.5">Dispatch Ref / Item</th>
                        <th className="p-3.5">Discrepancy Type</th>
                        <th className="p-3.5 text-center">Variance Qty</th>
                        <th className="p-3.5 text-center">Estimated Loss</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5">Supervisor Notes</th>
                        <th className="p-3.5 text-right">Resolution</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {(Array.isArray(reconciliations) ? reconciliations : []).map(rec => (
                        <tr key={rec.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                          <td className="p-3.5 text-gray-500 whitespace-nowrap">
                            {new Date(rec.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3.5">
                            <div className="font-bold text-gray-900 dark:text-white">
                              {rec.item_name || "Linen Item"}
                            </div>
                            <span className="text-[11px] text-gray-400">
                              {rec.dispatch_no ? `Dispatch: ${rec.dispatch_no}` : "Internal Physical Count Audit"}
                            </span>
                          </td>
                          <td className="p-3.5">
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold capitalize bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200">
                              {(rec.discrepancy_type || "shortage").replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-3.5 text-center font-bold text-rose-600">
                            {rec.discrepancy_qty || rec.quantity || 0} pcs
                          </td>
                          <td className="p-3.5 text-center font-bold">
                            {currency} {Number(rec.estimated_loss || rec.cost_impact || 0).toFixed(2)}
                          </td>
                          <td className="p-3.5 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(rec.status)}`}>
                              {(rec.status || "pending_review").replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-3.5 text-xs text-gray-500 max-w-xs truncate">
                            {rec.supervisor_notes || rec.evidence_notes || rec.notes || rec.reason || "—"}
                          </td>
                          <td className="p-3.5 text-right whitespace-nowrap">
                            {(rec.status === "pending_review" || rec.status === "pending") ? (
                              <button
                                onClick={() => {
                                  setSelectedReconciliation(rec);
                                  setResolveReconciliationForm({
                                    resolution_action: "approved_writeoff",
                                    supervisor_notes: ""
                                  });
                                  setModalType("resolve_reconciliation");
                                  setIsModalOpen(true);
                                }}
                                className="px-3 py-1.5 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition"
                              >
                                Review & Resolve
                              </button>
                            ) : (
                              <span className="text-xs font-semibold text-gray-400">Resolved</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(!Array.isArray(reconciliations) || reconciliations.length === 0) && (
                        <tr>
                          <td colSpan={8} className="p-8 text-center text-gray-400">
                            No discrepancy or write-off audits found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: REPORTS */}
          {tab === "reports" && (
            <div className="space-y-6">
              {/* Date Filter & Export Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-400">From:</span>
                    <input
                      type="date"
                      value={reportDateRange.from}
                      onChange={(e) => setReportDateRange({ ...reportDateRange, from: e.target.value })}
                      className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-400">To:</span>
                    <input
                      type="date"
                      value={reportDateRange.to}
                      onChange={(e) => setReportDateRange({ ...reportDateRange, to: e.target.value })}
                      className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>
                  <button
                    onClick={fetchTabData}
                    className="px-3 py-2 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition"
                  >
                    Generate Report
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportCSV}
                    className="px-3 py-2 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-xs font-bold flex items-center gap-1.5 hover:bg-gray-50 transition"
                  >
                    <IconDownload size={15} /> Export CSV
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="px-3 py-2 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-xs font-bold flex items-center gap-1.5 hover:bg-gray-50 transition"
                  >
                    <IconPrinter size={15} /> Print
                  </button>
                </div>
              </div>

              {reportsData && (
                <div className="space-y-6">
                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 shadow-sm">
                      <span className="text-[11px] font-bold text-gray-400 uppercase">Total Dispatched Pieces</span>
                      <div className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                        {reportsData.summary?.total_dispatched_qty || 0}
                      </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 shadow-sm">
                      <span className="text-[11px] font-bold text-emerald-600 uppercase">Received Clean Pieces</span>
                      <div className="text-2xl font-black text-emerald-600 mt-1">
                        {reportsData.summary?.total_accepted_qty || 0}
                      </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 shadow-sm">
                      <span className="text-[11px] font-bold text-orange-600 uppercase">Rewash Rate</span>
                      <div className="text-2xl font-black text-orange-600 mt-1">
                        {reportsData.summary?.rewash_rate || 0}%
                      </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 shadow-sm">
                      <span className="text-[11px] font-bold text-rose-600 uppercase">Damaged / Discrepancy Rate</span>
                      <div className="text-2xl font-black text-rose-600 mt-1">
                        {reportsData.summary?.damage_rate || 0}%
                      </div>
                    </div>
                  </div>

                  {/* Vendor Turnaround Table */}
                  <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3">Vendor Performance & Turnaround Times</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs md:text-sm">
                        <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                          <tr>
                            <th className="p-3">Vendor</th>
                            <th className="p-3 text-center">Dispatches</th>
                            <th className="p-3 text-center">Avg Turnaround</th>
                            <th className="p-3 text-center">Total Invoiced</th>
                            <th className="p-3 text-center">Fulfillment Rate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                          {(reportsData.vendor_performance || []).map((vp, idx) => (
                            <tr key={idx}>
                              <td className="p-3 font-bold">{vp.vendor_name}</td>
                              <td className="p-3 text-center">{vp.dispatch_count}</td>
                              <td className="p-3 text-center font-bold text-purple-600">{vp.avg_turnaround_hours || 48} hrs</td>
                              <td className="p-3 text-center font-bold">{currency} {Number(vp.total_cost || 0).toFixed(2)}</td>
                              <td className="p-3 text-center">
                                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700">
                                  {vp.fulfillment_rate || 98}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 8: GUEST LAUNDRY ORDERS */}
          {tab === "guest_orders" && (
            <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs md:text-sm">
                  <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                    <tr>
                      <th className="p-3.5">Guest & Room</th>
                      <th className="p-3.5">Items</th>
                      <th className="p-3.5">Price</th>
                      <th className="p-3.5">Status</th>
                      <th className="p-3.5 text-right">Update Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                    {(Array.isArray(guestOrders) ? guestOrders : []).map(order => (
                      <tr key={order.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                        <td className="p-3.5 font-bold text-gray-900 dark:text-white">
                          {order.guest_name} <span className="text-xs text-gray-400">({order.room_name || `Room ${order.room_id || "N/A"}`})</span>
                        </td>
                        <td className="p-3.5 text-gray-600 dark:text-gray-300">{order.items}</td>
                        <td className="p-3.5 font-bold">{currency} {order.total_price}</td>
                        <td className="p-3.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="p-3.5 text-right">
                          <select
                            onChange={(e) => updateStatus("guest_order", order.id, e.target.value)}
                            value={order.status}
                            className="p-1.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs font-bold focus:outline-none"
                          >
                            <option value="Requested">Requested</option>
                            <option value="Collected">Collected</option>
                            <option value="Washing">Washing</option>
                            <option value="Drying">Drying</option>
                            <option value="Ready">Ready</option>
                            <option value="Delivered">Delivered</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                    {guestOrders.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-gray-400">
                          No guest laundry orders recorded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 9: IN-HOUSE BATCHES */}
          {tab === "batches" && (
            <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs md:text-sm">
                  <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                    <tr>
                      <th className="p-3.5">Batch Type</th>
                      <th className="p-3.5">Machine</th>
                      <th className="p-3.5">Weight</th>
                      <th className="p-3.5">Status</th>
                      <th className="p-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                    {(Array.isArray(batches) ? batches : []).map(b => (
                      <tr key={b.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                        <td className="p-3.5 font-bold text-gray-900 dark:text-white">{b.batch_type}</td>
                        <td className="p-3.5 text-gray-500">{b.machine_name || "N/A"}</td>
                        <td className="p-3.5 font-medium">{b.weight_kg} kg</td>
                        <td className="p-3.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(b.status)}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="p-3.5 text-right">
                          <select
                            onChange={(e) => updateStatus("batch", b.id, e.target.value)}
                            value={b.status}
                            className="p-1.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs font-bold focus:outline-none"
                          >
                            <option value="Washing">Washing</option>
                            <option value="Drying">Drying</option>
                            <option value="Ironing">Ironing</option>
                            <option value="Ready">Ready</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                    {batches.length === 0 && (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-gray-400">
                          No in-house laundry batches active.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 10: MACHINES & CHEMICALS */}
          {tab === "machines" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm overflow-hidden">
                <div className="p-4 bg-gray-50 dark:bg-zinc-800/60 font-bold text-gray-700 dark:text-gray-200 text-sm flex justify-between items-center">
                  <span>Washing & Drying Machines</span>
                  <button
                    onClick={() => { setModalType("machine"); setIsModalOpen(true); }}
                    className="px-2.5 py-1 rounded-xl bg-restro-green text-white text-xs font-bold flex items-center gap-1"
                  >
                    <IconPlus size={14} /> Add
                  </button>
                </div>
                <table className="w-full text-left text-xs md:text-sm">
                  <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                    {(Array.isArray(machines) ? machines : []).map(m => (
                      <tr key={m.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                        <td className="p-3.5 font-bold text-gray-900 dark:text-white">{m.name}</td>
                        <td className="p-3.5 text-gray-500">{m.type}</td>
                        <td className="p-3.5 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusColor(m.status)}`}>
                            {m.status || "Operational"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white dark:bg-zinc-900 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm overflow-hidden">
                <div className="p-4 bg-gray-50 dark:bg-zinc-800/60 font-bold text-gray-700 dark:text-gray-200 text-sm flex justify-between items-center">
                  <span>Chemicals & Detergents Stock</span>
                  <button
                    onClick={() => { setModalType("chemical"); setIsModalOpen(true); }}
                    className="px-2.5 py-1 rounded-xl bg-blue-600 text-white text-xs font-bold flex items-center gap-1"
                  >
                    <IconPlus size={14} /> Add
                  </button>
                </div>
                <table className="w-full text-left text-xs md:text-sm">
                  <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                    {(Array.isArray(chemicals) ? chemicals : []).map(c => (
                      <tr key={c.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                        <td className="p-3.5 font-bold text-gray-900 dark:text-white">{c.name}</td>
                        <td className="p-3.5 text-right font-black text-blue-600 dark:text-blue-400">
                          {c.stock} {c.unit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 11: STAFF UNIFORMS */}
          {tab === "uniforms" && (
            <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs md:text-sm">
                  <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                    <tr>
                      <th className="p-3.5">Staff Name</th>
                      <th className="p-3.5">Uniform Type</th>
                      <th className="p-3.5">Status</th>
                      <th className="p-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                    {(Array.isArray(uniforms) ? uniforms : []).map(u => (
                      <tr key={u.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                        <td className="p-3.5 font-bold text-gray-900 dark:text-white">{u.staff_name}</td>
                        <td className="p-3.5 text-gray-500">{u.uniform_type}</td>
                        <td className="p-3.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(u.status)}`}>
                            {u.status}
                          </span>
                        </td>
                        <td className="p-3.5 text-right">
                          <select
                            onChange={(e) => updateStatus("uniform", u.id, e.target.value)}
                            value={u.status}
                            className="p-1.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs font-bold focus:outline-none"
                          >
                            <option value="Issued">Issued</option>
                            <option value="Returned">Returned</option>
                            <option value="Damaged">Damaged</option>
                            <option value="Lost">Lost</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                    {uniforms.length === 0 && (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-gray-400">
                          No staff uniforms tracked yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================= MODALS SECTION ================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden my-8">
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-900/50">
              <h3 className="text-lg font-bold dark:text-white capitalize flex items-center gap-2">
                {modalType === "condition_transfer" && <><IconArrowsExchange className="text-restro-green" size={20} /> Transfer Linen Condition</>}
                {modalType === "add_item" && <><IconPlus className="text-restro-green" size={20} /> Add New Linen Item</>}
                {modalType === "edit_item" && <><IconEdit className="text-restro-green" size={20} /> Edit Linen Item</>}
                {modalType === "add_usage" && <><IconRefresh className="text-restro-green" size={20} /> Log Room Linen Exchange</>}
                {modalType === "add_vendor" && <><IconUsers className="text-restro-green" size={20} /> Register Laundry Vendor</>}
                {modalType === "edit_vendor" && <><IconEdit className="text-restro-green" size={20} /> Edit Laundry Vendor</>}
                {modalType === "new_dispatch" && <><IconTruckDelivery className="text-restro-green" size={20} /> Dispatch Laundry to Vendor</>}
                {modalType === "receive_delivery" && <><IconCheck className="text-restro-green" size={20} /> Receive Vendor Delivery</>}
                {modalType === "delivery_history" && <><IconEye className="text-blue-600" size={20} /> Dispatch Delivery Receipts</>}
                {modalType === "generate_bill" && <><IconFileInvoice className="text-emerald-600" size={20} /> Generate Vendor Invoice (AP)</>}
                {modalType === "vendor_rates" && <><IconFileText className="text-purple-600" size={20} /> Negotiated Rate Card</>}
                {modalType === "item_history" && <><IconClipboardList className="text-blue-600" size={20} /> Movement Audit Trail</>}
                {modalType === "report_reconciliation" && <><IconAlertTriangle className="text-amber-600" size={20} /> Report Discrepancy</>}
                {modalType === "resolve_reconciliation" && <><IconCheck className="text-restro-green" size={20} /> Resolve Discrepancy</>}
                {["guest_order", "batch", "machine", "chemical", "uniform"].includes(modalType) && `Add ${modalType.replace('_', ' ')}`}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="p-1 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <IconX size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[75vh] overflow-y-auto">
              {/* MODAL 1: CONDITION TRANSFER */}
              {modalType === "condition_transfer" && (
                <form onSubmit={handleConditionTransfer} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Select Linen Item</label>
                    <select
                      value={conditionTransferForm.itemId}
                      onChange={(e) => setConditionTransferForm({ ...conditionTransferForm, itemId: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-semibold"
                    >
                      {items.map(it => (
                        <option key={it.id} value={it.id}>
                          {it.name} (Clean: {it.clean_stock || 0}, Dirty: {it.dirty_stock || 0}, Rewash: {it.rewash_stock || 0}, Damaged: {it.damaged_stock || 0})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">From Condition</label>
                      <select
                        value={conditionTransferForm.fromCondition}
                        onChange={(e) => setConditionTransferForm({ ...conditionTransferForm, fromCondition: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm capitalize"
                      >
                        <option value="clean">Clean (Usable)</option>
                        <option value="issued">Issued (In Rooms)</option>
                        <option value="dirty">Dirty (Awaiting)</option>
                        <option value="rewash">Rewash (Rejected)</option>
                        <option value="damaged">Damaged (Under Repair)</option>
                        <option value="inspecting">Inspecting</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">To Condition</label>
                      <select
                        value={conditionTransferForm.toCondition}
                        onChange={(e) => setConditionTransferForm({ ...conditionTransferForm, toCondition: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm capitalize"
                      >
                        <option value="clean">Clean (Usable)</option>
                        <option value="dirty">Dirty (Awaiting)</option>
                        <option value="rewash">Rewash (Rejected)</option>
                        <option value="damaged">Damaged (Under Repair)</option>
                        <option value="missing">Missing / Written-off</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Quantity</label>
                    <input
                      type="number"
                      min="1"
                      required
                      value={conditionTransferForm.qty}
                      onChange={(e) => setConditionTransferForm({ ...conditionTransferForm, qty: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Movement Reason / Notes</label>
                    <input
                      type="text"
                      placeholder="e.g. Returned from in-house washing or room change"
                      value={conditionTransferForm.reason}
                      onChange={(e) => setConditionTransferForm({ ...conditionTransferForm, reason: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                    />
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    Confirm Stock Transfer
                  </button>
                </form>
              )}

              {/* MODAL 2: ADD / EDIT ITEM */}
              {(modalType === "add_item" || modalType === "edit_item") && (
                <form onSubmit={handleItemSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Item Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. King Bed Sheet, Bath Towel"
                        value={itemForm.name}
                        onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Category</label>
                      <select
                        value={itemForm.category}
                        onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      >
                        <option value="Bed Linen">Bed Linen</option>
                        <option value="Bath Linen">Bath Linen</option>
                        <option value="Table Linen">Table Linen</option>
                        <option value="Uniforms">Uniforms</option>
                        <option value="General">General</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Unit</label>
                      <input
                        type="text"
                        value={itemForm.unit}
                        onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial / Total Stock</label>
                      <input
                        type="number"
                        min="0"
                        value={itemForm.stock}
                        onChange={(e) => setItemForm({ ...itemForm, stock: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Min PAR Level (Alert Target)</label>
                      <input
                        type="number"
                        min="0"
                        value={itemForm.par_level}
                        onChange={(e) => setItemForm({ ...itemForm, par_level: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Replacement Unit Cost ({currency})</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={itemForm.unit_cost}
                        onChange={(e) => setItemForm({ ...itemForm, unit_cost: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    {selectedItem ? "Save Changes" : "Create Item"}
                  </button>
                </form>
              )}

              {/* MODAL 3: LOG ROOM USAGE */}
              {modalType === "add_usage" && (
                <form onSubmit={handleUsageSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Target Room (Optional)</label>
                      <select
                        value={usageForm.room_id}
                        onChange={(e) => {
                          const r = rooms.find(rm => rm.id == e.target.value);
                          setUsageForm({
                            ...usageForm,
                            room_id: e.target.value,
                            room_name: r ? `Room ${r.room_number || r.name}` : ""
                          });
                        }}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      >
                        <option value="">Select Room...</option>
                        {rooms.map(r => (
                          <option key={r.id} value={r.id}>Room {r.room_number || r.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Room / Department Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Room 102 or Banquets"
                        value={usageForm.room_name}
                        onChange={(e) => setUsageForm({ ...usageForm, room_name: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Exchange Action</label>
                      <select
                        value={usageForm.type}
                        onChange={(e) => setUsageForm({ ...usageForm, type: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      >
                        <option value="Issue">Issue Clean Linen (To Room)</option>
                        <option value="Collect">Collect Dirty Linen (From Room)</option>
                        <option value="Exchange">Direct Room Linen Exchange</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Staff In-Charge</label>
                      <input
                        type="text"
                        placeholder="Housekeeper Name"
                        value={usageForm.staff_name}
                        onChange={(e) => setUsageForm({ ...usageForm, staff_name: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="space-y-2 border-t border-gray-100 dark:border-zinc-800 pt-3">
                    <label className="block text-xs font-bold text-gray-500 uppercase">Items Exchanged</label>
                    {usageForm.items.map((row, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <select
                          value={row.item_id}
                          onChange={(e) => {
                            const selected = items.find(it => it.id == e.target.value);
                            const updated = [...usageForm.items];
                            updated[idx].item_id = e.target.value;
                            updated[idx].name = selected ? selected.name : "";
                            setUsageForm({ ...usageForm, items: updated });
                          }}
                          className="flex-1 p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        >
                          <option value="">Select Linen Item...</option>
                          {items.map(it => (
                            <option key={it.id} value={it.id}>{it.name} (Clean: {it.clean_stock || 0})</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          placeholder="Qty"
                          value={row.quantity}
                          onChange={(e) => {
                            const updated = [...usageForm.items];
                            updated[idx].quantity = e.target.value;
                            setUsageForm({ ...usageForm, items: updated });
                          }}
                          className="w-24 p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                        />
                        {usageForm.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setUsageForm({ ...usageForm, items: usageForm.items.filter((_, i) => i !== idx) });
                            }}
                            className="p-2 text-rose-500 hover:text-rose-700"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setUsageForm({
                          ...usageForm,
                          items: [...usageForm.items, { item_id: items[0]?.id || "", name: items[0]?.name || "", quantity: 1 }]
                        });
                      }}
                      className="text-xs font-bold text-restro-green hover:underline flex items-center gap-1"
                    >
                      <IconPlus size={14} /> Add Another Item
                    </button>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Notes</label>
                    <input
                      type="text"
                      placeholder="Remarks..."
                      value={usageForm.notes}
                      onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                    />
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    Save Room Usage Record
                  </button>
                </form>
              )}

              {/* MODAL 4: ADD / EDIT VENDOR */}
              {(modalType === "add_vendor" || modalType === "edit_vendor") && (
                <form onSubmit={handleVendorSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vendor / Agency Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Express Cleaners & Laundry Ltd"
                        value={vendorForm.name}
                        onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Phone</label>
                      <input
                        type="text"
                        value={vendorForm.phone}
                        onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label>
                      <input
                        type="email"
                        value={vendorForm.email}
                        onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Contact Person</label>
                      <input
                        type="text"
                        value={vendorForm.contact_person}
                        onChange={(e) => setVendorForm({ ...vendorForm, contact_person: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Link to Accounts Payable Supplier</label>
                      <select
                        value={vendorForm.supplier_id}
                        onChange={(e) => setVendorForm({ ...vendorForm, supplier_id: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      >
                        <option value="">Select Existing Supplier (Optional)...</option>
                        {suppliers.map(s => (
                          <option key={s.id} value={s.id}>{s.name} ({s.phone || "No phone"})</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Default Billing Basis</label>
                      <select
                        value={vendorForm.billing_basis}
                        onChange={(e) => setVendorForm({ ...vendorForm, billing_basis: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      >
                        <option value="per_piece">Per Piece</option>
                        <option value="per_kg">Per Kilogram (Kg)</option>
                        <option value="per_bundle">Per Bundle</option>
                        <option value="fixed_contract">Fixed Contract Fee</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Default Rate ({currency})</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={vendorForm.default_rate}
                        onChange={(e) => setVendorForm({ ...vendorForm, default_rate: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rate Contract Ref #</label>
                      <input
                        type="text"
                        placeholder="e.g. CTR-2026-LND"
                        value={vendorForm.rate_contract_ref}
                        onChange={(e) => setVendorForm({ ...vendorForm, rate_contract_ref: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Payment Terms</label>
                      <input
                        type="text"
                        placeholder="e.g. Net 15, Net 30"
                        value={vendorForm.payment_terms}
                        onChange={(e) => setVendorForm({ ...vendorForm, payment_terms: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Address & Details</label>
                      <input
                        type="text"
                        value={vendorForm.address}
                        onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    {selectedVendor ? "Save Vendor Changes" : "Register Vendor"}
                  </button>
                </form>
              )}

              {/* MODAL 5: NEW VENDOR DISPATCH */}
              {modalType === "new_dispatch" && (
                <form onSubmit={handleDispatchSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Laundry Vendor *</label>
                      <select
                        required
                        value={dispatchForm.vendor_id}
                        onChange={(e) => {
                          const v = vendors.find(vend => vend.id == e.target.value);
                          setDispatchForm({
                            ...dispatchForm,
                            vendor_id: e.target.value,
                            billing_basis: v ? (v.billing_basis || "per_piece") : "per_piece"
                          });
                        }}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-semibold"
                      >
                        <option value="">Select Vendor...</option>
                        {vendors.map(v => (
                          <option key={v.id} value={v.id}>{v.name} ({v.billing_basis || "per_piece"})</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Expected Return Date & Time *</label>
                      <input
                        type="datetime-local"
                        required
                        value={dispatchForm.expected_return_date}
                        onChange={(e) => setDispatchForm({ ...dispatchForm, expected_return_date: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vendor Challan / Ref #</label>
                      <input
                        type="text"
                        placeholder="e.g. CH-9902"
                        value={dispatchForm.challan_no}
                        onChange={(e) => setDispatchForm({ ...dispatchForm, challan_no: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Weight (kg) if billed by kg</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={dispatchForm.total_weight_kg}
                        onChange={(e) => setDispatchForm({ ...dispatchForm, total_weight_kg: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dispatching Staff</label>
                      <input
                        type="text"
                        placeholder="Name of staff"
                        value={dispatchForm.dispatch_person}
                        onChange={(e) => setDispatchForm({ ...dispatchForm, dispatch_person: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vendor Driver / Receiving Person</label>
                      <input
                        type="text"
                        placeholder="Vendor pickup person"
                        value={dispatchForm.receiving_person}
                        onChange={(e) => setDispatchForm({ ...dispatchForm, receiving_person: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Items List */}
                  <div className="space-y-2 border-t border-gray-100 dark:border-zinc-800 pt-3">
                    <label className="block text-xs font-bold text-gray-500 uppercase">Items Dispatched (Drawn from Dirty Stock)</label>
                    {dispatchForm.items.map((row, idx) => {
                      const selItem = items.find(it => it.id == row.item_id);
                      return (
                        <div key={idx} className="flex gap-2 items-center">
                          <select
                            value={row.item_id}
                            onChange={(e) => {
                              const it = items.find(x => x.id == e.target.value);
                              const updated = [...dispatchForm.items];
                              updated[idx].item_id = e.target.value;
                              updated[idx].unit = it ? it.unit : "Pcs";
                              setDispatchForm({ ...dispatchForm, items: updated });
                            }}
                            className="flex-1 p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                          >
                            <option value="">Select Linen Item...</option>
                            {items.map(it => (
                              <option key={it.id} value={it.id}>
                                {it.name} (Dirty Available: {it.dirty_stock || 0} {it.unit || "Pcs"})
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="1"
                            max={selItem ? (selItem.dirty_stock || 0) : undefined}
                            placeholder="Qty"
                            value={row.quantity}
                            onChange={(e) => {
                              const updated = [...dispatchForm.items];
                              updated[idx].quantity = e.target.value;
                              setDispatchForm({ ...dispatchForm, items: updated });
                            }}
                            className="w-24 p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                          />
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Rate"
                            value={row.unit_rate}
                            onChange={(e) => {
                              const updated = [...dispatchForm.items];
                              updated[idx].unit_rate = e.target.value;
                              setDispatchForm({ ...dispatchForm, items: updated });
                            }}
                            className="w-24 p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                            title="Rate per piece"
                          />
                          {dispatchForm.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                setDispatchForm({
                                  ...dispatchForm,
                                  items: dispatchForm.items.filter((_, i) => i !== idx)
                                });
                              }}
                              className="p-2 text-rose-500 hover:text-rose-700"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => {
                        setDispatchForm({
                          ...dispatchForm,
                          items: [...dispatchForm.items, { item_id: items[0]?.id || "", quantity: 1, unit: "Pcs", unit_rate: 0 }]
                        });
                      }}
                      className="text-xs font-bold text-restro-green hover:underline flex items-center gap-1"
                    >
                      <IconPlus size={14} /> Add Line Item
                    </button>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Pre-existing Damage / Inspection Notes</label>
                    <input
                      type="text"
                      placeholder="e.g. 2 bedsheets have existing tears on dispatch"
                      value={dispatchForm.pre_damage_notes}
                      onChange={(e) => setDispatchForm({ ...dispatchForm, pre_damage_notes: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm"
                    />
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    Create Dispatch & Deduct Dirty Stock
                  </button>
                </form>
              )}

              {/* MODAL 6: MULTI-DELIVERY PARTIAL RECEIPT */}
              {modalType === "receive_delivery" && selectedDispatch && (
                <form onSubmit={handleDeliveryReceiptSubmit} className="space-y-4">
                  <div className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 text-xs">
                    <p className="font-bold">
                      Receiving delivery for Dispatch #{selectedDispatch.dispatch_no || selectedDispatch.id} • {selectedDispatch.vendor_name}
                    </p>
                    <p className="text-gray-400">
                      Dispatched items will be categorized into Clean (ready for service), Rewash (rejected), Damaged (vendor compensation), or Missing.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Delivery Receipt / DC #</label>
                      <input
                        type="text"
                        value={deliveryReceiptForm.receipt_no}
                        onChange={(e) => setDeliveryReceiptForm({ ...deliveryReceiptForm, receipt_no: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Delivery Person (Vendor)</label>
                      <input
                        type="text"
                        placeholder="Driver name"
                        value={deliveryReceiptForm.vendor_delivery_person}
                        onChange={(e) => setDeliveryReceiptForm({ ...deliveryReceiptForm, vendor_delivery_person: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                      />
                    </div>
                  </div>

                  {/* Line Items Receive Matrix */}
                  <div className="space-y-3 border-t border-gray-100 dark:border-zinc-800 pt-3">
                    <label className="block text-xs font-bold text-gray-500 uppercase">Received Condition Breakdown</label>
                    {deliveryReceiptForm.items.map((it, idx) => (
                      <div key={idx} className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900/60 border border-gray-200/50 dark:border-zinc-800 space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-gray-900 dark:text-white">{it.name}</span>
                          <span className="text-purple-600 font-bold">
                            Outstanding: {it.outstanding_qty} {it.unit || "Pcs"} (Total Dispatched: {it.dispatched_qty})
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <span className="text-[10px] font-bold text-emerald-600 uppercase">Accepted Clean</span>
                            <input
                              type="number"
                              min="0"
                              value={it.accepted_clean_qty}
                              onChange={(e) => {
                                const updated = [...deliveryReceiptForm.items];
                                updated[idx].accepted_clean_qty = e.target.value;
                                setDeliveryReceiptForm({ ...deliveryReceiptForm, items: updated });
                              }}
                              className="w-full p-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold text-emerald-600"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-orange-600 uppercase">Rewash</span>
                            <input
                              type="number"
                              min="0"
                              value={it.rewash_qty}
                              onChange={(e) => {
                                const updated = [...deliveryReceiptForm.items];
                                updated[idx].rewash_qty = e.target.value;
                                setDeliveryReceiptForm({ ...deliveryReceiptForm, items: updated });
                              }}
                              className="w-full p-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold text-orange-600"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-rose-600 uppercase">Damaged</span>
                            <input
                              type="number"
                              min="0"
                              value={it.damaged_qty}
                              onChange={(e) => {
                                const updated = [...deliveryReceiptForm.items];
                                updated[idx].damaged_qty = e.target.value;
                                setDeliveryReceiptForm({ ...deliveryReceiptForm, items: updated });
                              }}
                              className="w-full p-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold text-rose-600"
                            />
                          </div>
                          <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase">Missing / Shortage</span>
                            <input
                              type="number"
                              min="0"
                              value={it.missing_qty}
                              onChange={(e) => {
                                const updated = [...deliveryReceiptForm.items];
                                updated[idx].missing_qty = e.target.value;
                                setDeliveryReceiptForm({ ...deliveryReceiptForm, items: updated });
                              }}
                              className="w-full p-2 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold text-gray-400"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Receipt Inspection Notes</label>
                    <input
                      type="text"
                      placeholder="e.g. 5 pillow covers returned damp, sent to drying"
                      value={deliveryReceiptForm.notes}
                      onChange={(e) => setDeliveryReceiptForm({ ...deliveryReceiptForm, notes: e.target.value })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    Confirm Receipt & Update Inventory Condition
                  </button>
                </form>
              )}

              {/* MODAL 7: DELIVERY HISTORY */}
              {modalType === "delivery_history" && selectedDispatch && (
                <div className="space-y-4">
                  <div className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 text-xs">
                    <p className="font-bold">
                      Delivery History for Dispatch #{selectedDispatch.dispatch_no || selectedDispatch.id}
                    </p>
                    <p className="text-gray-400">
                      Total Dispatched: {selectedDispatch.total_dispatched_qty || selectedDispatch.total_qty || 0} pcs • Remaining Outstanding: {selectedDispatch.total_outstanding_qty || 0} pcs
                    </p>
                  </div>

                  <div className="space-y-3">
                    {dispatchReceipts.map((rc, idx) => (
                      <div key={idx} className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200/60 dark:border-zinc-800 text-xs space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900 dark:text-white">Receipt #{rc.receipt_no || rc.id}</span>
                          <span className="text-gray-400">{new Date(rc.delivery_date).toLocaleString()}</span>
                        </div>
                        <div className="grid grid-cols-4 gap-2 pt-1 border-t border-gray-50 dark:border-zinc-800">
                          <div>Accepted: <span className="font-bold text-emerald-600">{rc.total_accepted_qty}</span></div>
                          <div>Rewash: <span className="font-bold text-orange-600">{rc.total_rewash_qty}</span></div>
                          <div>Damaged: <span className="font-bold text-rose-600">{rc.total_damaged_qty}</span></div>
                          <div>Missing: <span className="font-bold text-gray-400">{rc.total_missing_qty}</span></div>
                        </div>
                        {rc.notes && <p className="text-[11px] text-gray-500 italic">Notes: {rc.notes}</p>}
                      </div>
                    ))}
                    {dispatchReceipts.length === 0 && (
                      <p className="text-center text-gray-400 py-6 text-xs">No partial deliveries received yet for this dispatch.</p>
                    )}
                  </div>
                </div>
              )}

              {/* MODAL 8: GENERATE ACCOUNTS PAYABLE BILL */}
              {modalType === "generate_bill" && selectedDispatch && (
                <form onSubmit={handleVendorBillSubmit} className="space-y-4">
                  <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 text-xs">
                    <p className="font-bold text-emerald-800 dark:text-emerald-300">
                      Create Accounts Payable Bill for Vendor: {selectedDispatch.vendor_name}
                    </p>
                    <p className="text-emerald-700 dark:text-emerald-400">
                      This will insert an official invoice into the Purchases ledger with status "unpaid" so finance can process payment.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Invoice / Bill # *</label>
                      <input
                        type="text"
                        required
                        value={vendorBillForm.invoice_no}
                        onChange={(e) => setVendorBillForm({ ...vendorBillForm, invoice_no: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Invoice Date *</label>
                      <input
                        type="date"
                        required
                        value={vendorBillForm.invoice_date}
                        onChange={(e) => setVendorBillForm({ ...vendorBillForm, invoice_date: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Bill Amount ({currency}) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={vendorBillForm.amount}
                        onChange={(e) => setVendorBillForm({ ...vendorBillForm, amount: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-black text-emerald-600"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Notes</label>
                      <input
                        type="text"
                        value={vendorBillForm.notes}
                        onChange={(e) => setVendorBillForm({ ...vendorBillForm, notes: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                      />
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition mt-2">
                    Create Invoice in Accounts Payable
                  </button>
                </form>
              )}

              {/* MODAL 9: VENDOR RATE CARDS */}
              {modalType === "vendor_rates" && selectedVendor && (
                <form onSubmit={handleSaveVendorRates} className="space-y-4">
                  <div className="p-3 rounded-2xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-900 text-xs">
                    <p className="font-bold text-purple-800 dark:text-purple-300">
                      Negotiated Rate Card for: {selectedVendor.name}
                    </p>
                    <p className="text-purple-700 dark:text-purple-400">
                      Configure negotiated washing and laundering charges per item.
                    </p>
                  </div>

                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {vendorRatesList.map((rt, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 dark:bg-zinc-900 text-xs">
                        <div className="flex-1 font-bold text-gray-900 dark:text-white">
                          {rt.name}
                          <span className="block text-[10px] text-gray-400 font-normal">{rt.category || "General"}</span>
                        </div>
                        <select
                          value={rt.billing_basis}
                          onChange={(e) => {
                            const updated = [...vendorRatesList];
                            updated[idx].billing_basis = e.target.value;
                            setVendorRatesList(updated);
                          }}
                          className="p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-[11px]"
                        >
                          <option value="per_piece">Per Piece</option>
                          <option value="per_kg">Per Kg</option>
                          <option value="per_bundle">Per Bundle</option>
                        </select>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">{currency}</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={rt.rate}
                            onChange={(e) => {
                              const updated = [...vendorRatesList];
                              updated[idx].rate = e.target.value;
                              setVendorRatesList(updated);
                            }}
                            className="w-20 p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent font-bold text-xs"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white transition mt-2">
                    Save Rate Card
                  </button>
                </form>
              )}

              {/* MODAL 10: MOVEMENT AUDIT TRAIL */}
              {modalType === "item_history" && selectedItem && (
                <div className="space-y-4">
                  <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 text-xs">
                    <p className="font-bold text-blue-800 dark:text-blue-300">
                      Condition Transition Audit Trail for: {selectedItem.name}
                    </p>
                    <p className="text-blue-700 dark:text-blue-400">
                      Total Stock: {selectedItem.stock} • Clean: {selectedItem.clean_stock || 0} • Dirty: {selectedItem.dirty_stock || 0}
                    </p>
                  </div>

                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {(Array.isArray(itemMovements) ? itemMovements : []).map((mv, idx) => (
                      <div key={idx} className="p-2.5 rounded-xl bg-gray-50 dark:bg-zinc-900 text-xs flex justify-between items-center">
                        <div>
                          <span className="font-bold capitalize">{mv.movement_type || "Transfer"}</span>
                          <p className="text-[11px] text-gray-400">
                            {mv.from_condition || "Initial"} → {mv.to_condition || "Stock"} • {mv.notes || "No notes"}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-emerald-600">Qty: {mv.qty}</span>
                          <span className="block text-[10px] text-gray-400">{new Date(mv.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                    {itemMovements.length === 0 && (
                      <p className="text-center text-gray-400 py-6 text-xs">No movement history logged yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* MODAL 11: REPORT DISCREPANCY */}
              {modalType === "report_reconciliation" && (
                <form onSubmit={handleCreateReconciliation} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Select Linen Item *</label>
                    <select
                      required
                      value={reconciliationForm.item_id}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, item_id: e.target.value })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                    >
                      {items.map(it => (
                        <option key={it.id} value={it.id}>{it.name} (Missing: {it.missing_stock || 0})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Discrepancy Type</label>
                    <select
                      value={reconciliationForm.discrepancy_type}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, discrepancy_type: e.target.value })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    >
                      <option value="shortage">Shortage / Missing from Vendor Delivery</option>
                      <option value="vendor_damage">Vendor Caused Damage / Stain</option>
                      <option value="internal_loss">Internal Room / Floor Loss</option>
                      <option value="rewash_discrepancy">Excessive Rewash Rejections</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Variance Quantity *</label>
                      <input
                        type="number"
                        min="1"
                        required
                        value={reconciliationForm.discrepancy_qty}
                        onChange={(e) => setReconciliationForm({ ...reconciliationForm, discrepancy_qty: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Estimated Loss Value ({currency})</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={reconciliationForm.estimated_loss}
                        onChange={(e) => setReconciliationForm({ ...reconciliationForm, estimated_loss: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Audit Notes & Findings</label>
                    <input
                      type="text"
                      placeholder="Observation details..."
                      value={reconciliationForm.notes}
                      onChange={(e) => setReconciliationForm({ ...reconciliationForm, notes: e.target.value })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-amber-600 hover:bg-amber-700 text-white transition mt-2">
                    Submit Discrepancy for Supervisor Review
                  </button>
                </form>
              )}

              {/* MODAL 12: RESOLVE RECONCILIATION */}
              {modalType === "resolve_reconciliation" && selectedReconciliation && (
                <form onSubmit={handleResolveReconciliation} className="space-y-4">
                  <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 text-xs">
                    <p className="font-bold text-amber-800 dark:text-amber-300">
                      Review Discrepancy for: {selectedReconciliation.item_name}
                    </p>
                    <p className="text-amber-700 dark:text-amber-400">
                      Variance: {selectedReconciliation.discrepancy_qty} pcs • Estimated Loss: {currency} {selectedReconciliation.estimated_loss || 0}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Supervisor Resolution Action</label>
                    <select
                      value={resolveReconciliationForm.resolution_action}
                      onChange={(e) => setResolveReconciliationForm({ ...resolveReconciliationForm, resolution_action: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                    >
                      <option value="approved_writeoff">Approve Write-off (Deduct missing stock from asset value)</option>
                      <option value="billed_to_vendor">Bill / Debit Vendor (Deduct from Accounts Payable)</option>
                      <option value="replaced_by_vendor">Replaced by Vendor (Restore to usable clean stock)</option>
                      <option value="rejected">Reject Claim (Variance accounted for)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Supervisor Authorization Justification *</label>
                    <textarea
                      required
                      rows={3}
                      placeholder="Enter supervisor audit notes and reason for approval..."
                      value={resolveReconciliationForm.supervisor_notes}
                      onChange={(e) => setResolveReconciliationForm({ ...resolveReconciliationForm, supervisor_notes: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition mt-2">
                    Authorize Resolution
                  </button>
                </form>
              )}

              {/* MODALS FOR GUEST ORDER / BATCH / MACHINE / CHEMICAL / UNIFORM */}
              {modalType === "guest_order" && (
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <input type="text" placeholder="Room Number (optional)" value={newGuestOrderForm.room_id} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, room_id: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="text" required placeholder="Guest Name" value={newGuestOrderForm.guest_name} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, guest_name: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="text" required placeholder="Items (e.g. 2 Shirts, 1 Trouser)" value={newGuestOrderForm.items} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, items: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="number" required placeholder={`Total Price (${currency})`} value={newGuestOrderForm.total_price} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, total_price: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Record</button>
                </form>
              )}

              {modalType === "batch" && (
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <select value={newBatchForm.batch_type} onChange={e=>setNewBatchForm({...newBatchForm, batch_type: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm">
                    <option value="Washing">Washing</option><option value="Drying">Drying</option><option value="Ironing">Ironing</option>
                  </select>
                  <input type="number" required placeholder="Weight (kg)" value={newBatchForm.weight_kg} onChange={e=>setNewBatchForm({...newBatchForm, weight_kg: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <select value={newBatchForm.machine_id} onChange={e=>setNewBatchForm({...newBatchForm, machine_id: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm">
                    <option value="">Select Machine...</option>
                    {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Record</button>
                </form>
              )}

              {modalType === "machine" && (
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <input type="text" required placeholder="Machine Name (e.g. Washer 01)" value={newMachineForm.name} onChange={e=>setNewMachineForm({...newMachineForm, name: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <select value={newMachineForm.type} onChange={e=>setNewMachineForm({...newMachineForm, type: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm">
                    <option value="Washer">Washer</option><option value="Dryer">Dryer</option><option value="Ironer">Ironer</option>
                  </select>
                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Machine</button>
                </form>
              )}

              {modalType === "chemical" && (
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <input type="text" required placeholder="Chemical Name (e.g. Bleach, Softener)" value={newChemicalForm.name} onChange={e=>setNewChemicalForm({...newChemicalForm, name: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <div className="grid grid-cols-2 gap-4">
                    <input type="number" required placeholder="Stock" value={newChemicalForm.stock} onChange={e=>setNewChemicalForm({...newChemicalForm, stock: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                    <input type="text" required placeholder="Unit (Liters, Kg)" value={newChemicalForm.unit} onChange={e=>setNewChemicalForm({...newChemicalForm, unit: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  </div>
                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Chemical</button>
                </form>
              )}

              {modalType === "uniform" && (
                <form onSubmit={handleGeneralSubmit} className="space-y-4">
                  <input type="text" required placeholder="Staff Member Name" value={newUniformForm.staff_name} onChange={e=>setNewUniformForm({...newUniformForm, staff_name: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="text" required placeholder="Uniform Type (e.g. Chef Coat, Housekeeper Apron)" value={newUniformForm.uniform_type} onChange={e=>setNewUniformForm({...newUniformForm, uniform_type: e.target.value})} className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Issue Uniform</button>
                </form>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
